import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Badge,
  InputGroup,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { FaDownload, FaFileExport } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { BASE_URL, HEADERS } from "../../utils/constant";
import { useCommonContext } from "../../utils/context";
import ExportModal from "../../components/Modals/AttendenceExportModel";
import Loader from "../../components/Loader";
import CommonButton from "../../components/CommonButton";
import NoData from "../../components/NoData";
import FetchLogsModal from "../../components/Modals/FetchLogsModal";
import "react-calendar/dist/Calendar.css";
import "bootstrap/dist/css/bootstrap.min.css";

const CheckInOut = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [punchInDate, setPunchInDate] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [singleDate, setSingleDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const { fetchAllusers } = useCommonContext;
  const [showFetchLogsModal, setShowFetchLogsModal] = useState(false);
  const userPermissions = localStorage.getItem("user_permissions") || "";
  const userId = localStorage.getItem("userId");
  const { user } = useCommonContext();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAttendanceData();
  }, []);

  const fetchAttendanceData = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("user_id", userId);

      if (filters.fromDate && filters.toDate) {
        params.append("from_date", filters.fromDate);
        params.append("to_date", filters.toDate);
      } else if (filters.singleDate) {
        params.append("date", filters.singleDate);
      }

      const response = await axios.get(
        `${BASE_URL}/users/attendance/?${params}`,
        { headers: HEADERS }
      );

      const data = response.data || [];

      setFilteredData(data);

      setPunchInDate(response.data[0]?.latest_punch_in || null);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Error fetching attendance data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInOut = async (action) => {
    try {
      const endpoint = action === "punch_in" ? "punch_in" : "punch_out";
      const response = await axios.post(
        `${BASE_URL}/users/attendance/${endpoint}/`,
        {},
        { headers: HEADERS }
      );

      if (action === "punch_in") {
        localStorage.setItem("punch_in", new Date().toISOString());
      } else if (action === "punch_out") {
        localStorage.clear();
        navigate("/signin");
      }

      fetchAttendanceData();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Error performing attendance action"
      );
    }
  };

  const handleFilter = () => {
    if (singleDate) {
      fetchAttendanceData({ singleDate });
      return;
    }
    if (!fromDate || !toDate) {
      toast.info("Please select both From and To dates or a single date");
      return;
    }
    fetchAttendanceData({ fromDate, toDate });
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setSingleDate("");
    fetchAttendanceData();
  };

  const handleViewLogs = (item) => {
    setSelectedLogs(item.logs || []);
    setSelectedDate(item.date);
    setShowLogsModal(true);
  };

  const formatTime = (datetime) => {
    if (!datetime) return "--";
    const date = new Date(datetime);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const today = moment().format("YYYY-MM-DD");

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <h4 className="fw-semibold mb-md-0">
          Welcome, {user?.first_name || "User"}
        </h4>

        <div className="d-flex flex-wrap gap-2 mt-3 mt-md-0">
          {localStorage.getItem("attendence_type") !== "machine" && (
            <>
              {!punchInDate &&
                userPermissions.includes("attendance_mark_attendance") && (
                  <Button
                    variant="primary"
                    className="px-4 py-2"
                    style={{
                      minWidth: "120px",
                      height: "35px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleCheckInOut("punch_in")}
                  >
                    Punch In
                  </Button>
                )}

              {userPermissions.includes("attendance_mark_attendance") && (
                <Button
                  variant="danger"
                  className="px-4 "
                  style={{
                    minWidth: "120px",
                    height: "35px",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => handleCheckInOut("punch_out")}
                >
                  Punch Out
                </Button>
              )}
            </>
          )}

          {userPermissions.includes("attendance_export_own_attendance") && (
            <CommonButton
              className="uniform-button"
              onClick={() => setShowExportModal(true)}
            >
              <FaFileExport style={{ marginRight: "6px" }} /> Export
            </CommonButton>
          )}

          <CommonButton
            className="uniform-button"
            onClick={() => setShowFetchLogsModal(true)}
          >
            <FaDownload className="me-1" /> Fetch Logs
          </CommonButton>
        </div>
      </div>

      <Card className="shadow-sm mb-4 border-0">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col md={3}>
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setSingleDate("");
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setSingleDate("");
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Single Date</Form.Label>
              <InputGroup>
                <Form.Control
                  type="date"
                  value={singleDate}
                  onChange={(e) => {
                    setSingleDate(e.target.value);
                    setFromDate("");
                    setToDate("");
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex">
              <Button className="me-2" variant="primary" onClick={handleFilter}>
                Apply
              </Button>
              <Button variant="outline-secondary" onClick={handleClear}>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light fw-semibold">
          Attendance Logs
        </Card.Header>
        <Card.Body>
          {loading ? (
            <Loader />
          ) : filteredData.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Punch In / Out</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => {
                    const punchIn = item.latest_punch_in
                      ? formatTime(item.latest_punch_in)
                      : "--";
                    const punchOut = item.latest_punch_out
                      ? formatTime(item.latest_punch_out)
                      : "--";

                    return (
                      <tr key={index}>
                        <td>
                          {moment(item.date).format("DD MMM YYYY")}
                          {item.date === today && (
                            <Badge bg="primary" className="ms-2">
                              Today
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge
                            bg={
                              item.status === "present"
                                ? "success"
                                : item.status === "absent"
                                ? "danger"
                                : item.status === "onleave"
                                ? "warning"
                                : "secondary"
                            }
                          >
                            {item.status || "N/A"}
                          </Badge>
                        </td>
                        <td>
                          {punchIn} <span className="text-muted">/</span>{" "}
                          {punchOut}
                        </td>
                        <td>
                          {item.logs?.length >= 1 ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-primary text-decoration-none"
                              onClick={() => handleViewLogs(item)}
                            >
                              View Logs
                            </Button>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData message="No attendance logs available." />
          )}
        </Card.Body>
      </Card>
      {showExportModal && (
        <ExportModal
          show={showExportModal}
          onHide={() => setShowExportModal(false)}
        />
      )}

      <FetchLogsModal
        show={showFetchLogsModal}
        handleClose={() => setShowFetchLogsModal(false)}
        onSuccess={fetchAttendanceData}
      />
      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 mb-0">
            All Logs — {moment(selectedDate).format("DD MMM YYYY")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {selectedLogs.length ? (
            <ul className="mb-0 bg-light p-2 rounded">
              {Array.from({ length: Math.ceil(selectedLogs.length / 2) }).map(
                (_, i) => {
                  const inLog = selectedLogs[i * 2];
                  const outLog = selectedLogs[i * 2 + 1];
                  const inTime = moment(inLog).format("HH:mm:ss");
                  const outTime = outLog
                    ? moment(outLog).format("HH:mm:ss")
                    : "--";
                  return (
                    <li
                      key={i}
                      style={{ padding: "4px 0", fontSize: "0.9rem" }}
                    >
                      {inTime} <span className="text-muted"> - </span> {outTime}
                    </li>
                  );
                }
              )}
            </ul>
          ) : (
            <div className="text-center text-muted small">
              No logs available
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CheckInOut;
